import { Loader2 } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Typography } from '@/components/ui/typography';
import { useRecommendationContext } from '@/contexts/recommendation-context';

import { BudgetRecommendationForm } from './budget-recommendation-form';
import { GeneralRecommendationForm } from './general-recommendation-form';

interface RecommendationPostingDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function RecommendationPostingDialog({
  isOpen,
  setIsOpen,
}: RecommendationPostingDialogProps) {
  const {
    recommendationType,
    opening,
    hiringGoal,
    recommendedChannels,
    budgetRecommendation,
    isSubmitting,
    selectedChannels,
  } = useRecommendationContext();

  const isSubmitDisabled =
    isSubmitting ||
    (recommendationType === 'general' && selectedChannels.length === 0);

  if (!opening) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="min-w-[372px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              defaultMessage="Review recommended changes"
              description="Modal title before applying sourcing budget or channel updates"
            />
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1">
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="These updates will be applied to this opening."
                description="Intro line above the job title in the confirmation modal"
              />
            </Typography>
            <Typography variant="body2">{opening.title}</Typography>
          </div>

          <div className="bg-fx-neutral-200 h-px w-full" />

          {recommendationType === 'general' && recommendedChannels && (
            <GeneralRecommendationForm
              opening={opening}
              hiringGoal={hiringGoal}
              recommendedChannels={recommendedChannels}
            />
          )}

          {recommendationType === 'budget' && budgetRecommendation && (
            <BudgetRecommendationForm
              hiringGoal={hiringGoal}
              budgetRecommendation={budgetRecommendation}
            />
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="submit"
            form="recommendation-form"
            disabled={isSubmitDisabled}
          >
            {isSubmitting && <Loader2 className="size-4 animate-spin" />}
            <FormattedMessage
              defaultMessage="Confirm & Apply"
              description="Primary submit for the recommendation posting dialog form"
            />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
